/**
 * LocationContext — web port of `apps/WunderGuide/context/LocationContext.tsx`.
 *
 * Browser geolocation has fewer knobs than expo-location, so we map RN's
 * behaviour onto `navigator.geolocation`:
 *   • `requestPermission` → first `getCurrentPosition` call (browser prompts)
 *   • `startWatchingLocation` → `watchPosition`
 *   • `enterHighPrecision` → `watchPosition({ enableHighAccuracy: true })`
 *   • `enterBaselinePrecision` → switch back to default options
 *
 * Surface kept compatible with RN so callers can import `useLocation` and
 * call the same methods.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { httpClient } from '../http';

const LOCATION_API = '/functions/v1/locations';
const STALE_AFTER_MS = 90_000;

export interface LocationCoords {
  latitude: number;
  longitude: number;
  accuracy?: number;
  altitude?: number;
  altitudeAccuracy?: number;
  heading?: number;
  speed?: number;
}

export type PermissionStatus =
  | 'granted'
  | 'denied'
  | 'permanently_denied'
  | 'unknown';

interface LocationContextType {
  location: LocationCoords | null;
  lastUpdatedAt: number | null;
  isStale: boolean;
  loading: boolean;
  error: string | null;

  heading: number | null;
  headingAccuracy: number | null;

  hasPermission: boolean | null;
  permissionStatus: PermissionStatus;
  isRequesting: boolean;
  requiresSettingsAccess: boolean;

  checkPermission: () => Promise<boolean>;
  requestPermission: () => Promise<boolean>;
  refreshLocation: () => Promise<void>;
  startWatchingLocation: () => Promise<void>;
  stopWatchingLocation: () => void;
  startWatchingHeading: () => Promise<void>;
  stopWatchingHeading: () => void;
  isWatchingHeading: boolean;
  ensureFreshLocation: () => Promise<void>;
  enterHighPrecision: (durationMs?: number) => Promise<void>;
  enterBaselinePrecision: () => Promise<void>;
  ensurePreciseFix: (timeoutMs?: number) => Promise<void>;
  getLocation: (params: { search?: string; coords?: number[] }) => Promise<any>;
}

const LocationContext = createContext<LocationContextType | undefined>(
  undefined,
);

function coordsFrom(pos: GeolocationPosition): LocationCoords {
  const c = pos.coords;
  return {
    latitude: c.latitude,
    longitude: c.longitude,
    accuracy: c.accuracy,
    altitude: c.altitude ?? undefined,
    altitudeAccuracy: c.altitudeAccuracy ?? undefined,
    heading: c.heading ?? undefined,
    speed: c.speed ?? undefined,
  };
}

export const LocationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [location, setLocation] = useState<LocationCoords | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [permissionStatus, setPermissionStatus] =
    useState<PermissionStatus>('unknown');
  const [isRequesting, setIsRequesting] = useState(false);
  const [heading, setHeading] = useState<number | null>(null);
  const [headingAccuracy, setHeadingAccuracy] = useState<number | null>(null);
  const [isWatchingHeading, setIsWatchingHeading] = useState(false);

  const watchIdRef = useRef<number | null>(null);
  const headingHandlerRef = useRef<
    ((e: DeviceOrientationEvent) => void) | null
  >(null);
  const highPrecisionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const isStale =
    lastUpdatedAt !== null && Date.now() - lastUpdatedAt > STALE_AFTER_MS;
  const requiresSettingsAccess = permissionStatus === 'permanently_denied';

  const supported =
    typeof navigator !== 'undefined' && 'geolocation' in navigator;

  const checkPermission = useCallback(async () => {
    if (!supported) {
      setPermissionStatus('denied');
      setHasPermission(false);
      return false;
    }
    try {
      // Permissions API isn't universal, but where present it tells us granted/denied/prompt
      const perms = (navigator as any).permissions;
      if (perms?.query) {
        const result = await perms.query({ name: 'geolocation' as any });
        const status: PermissionStatus =
          result.state === 'granted'
            ? 'granted'
            : result.state === 'denied'
              ? 'permanently_denied'
              : 'unknown';
        setPermissionStatus(status);
        setHasPermission(status === 'granted');
        return status === 'granted';
      }
    } catch {
      /* ignore */
    }
    return hasPermission === true;
  }, [supported, hasPermission]);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!supported) return false;
    setIsRequesting(true);
    return new Promise(resolve => {
      navigator.geolocation.getCurrentPosition(
        pos => {
          setLocation(coordsFrom(pos));
          setLastUpdatedAt(Date.now());
          setPermissionStatus('granted');
          setHasPermission(true);
          setError(null);
          setIsRequesting(false);
          resolve(true);
        },
        err => {
          const denied = err.code === err.PERMISSION_DENIED;
          setPermissionStatus(denied ? 'permanently_denied' : 'denied');
          setHasPermission(false);
          setError(err.message);
          setIsRequesting(false);
          resolve(false);
        },
        { enableHighAccuracy: false, timeout: 10_000, maximumAge: 60_000 },
      );
    });
  }, [supported]);

  const refreshLocation = useCallback(async () => {
    if (!supported) return;
    if (!hasPermission) {
      const granted = await requestPermission();
      if (!granted) return;
    }
    setLoading(true);
    return new Promise<void>(resolve => {
      navigator.geolocation.getCurrentPosition(
        pos => {
          setLocation(coordsFrom(pos));
          setLastUpdatedAt(Date.now());
          setError(null);
          setLoading(false);
          resolve();
        },
        err => {
          setError(err.message);
          setLoading(false);
          resolve();
        },
        { enableHighAccuracy: false, timeout: 10_000, maximumAge: 30_000 },
      );
    });
  }, [supported, hasPermission, requestPermission]);

  const startWatchingLocation = useCallback(async () => {
    if (!supported) return;
    if (watchIdRef.current !== null) return;
    if (!hasPermission) {
      const granted = await requestPermission();
      if (!granted) return;
    }
    watchIdRef.current = navigator.geolocation.watchPosition(
      pos => {
        setLocation(coordsFrom(pos));
        setLastUpdatedAt(Date.now());
        setError(null);
      },
      err => setError(err.message),
      { enableHighAccuracy: false, timeout: 20_000, maximumAge: 30_000 },
    );
  }, [supported, hasPermission, requestPermission]);

  const stopWatchingLocation = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
  }, []);

  const ensureFreshLocation = useCallback(async () => {
    if (!location || isStale) await refreshLocation();
  }, [location, isStale, refreshLocation]);

  const enterHighPrecision = useCallback(
    async (durationMs = 30_000) => {
      stopWatchingLocation();
      if (!supported) return;
      if (!hasPermission) {
        const granted = await requestPermission();
        if (!granted) return;
      }
      watchIdRef.current = navigator.geolocation.watchPosition(
        pos => {
          setLocation(coordsFrom(pos));
          setLastUpdatedAt(Date.now());
        },
        err => setError(err.message),
        { enableHighAccuracy: true, timeout: 10_000, maximumAge: 5_000 },
      );
      if (highPrecisionTimeoutRef.current) {
        clearTimeout(highPrecisionTimeoutRef.current);
      }
      highPrecisionTimeoutRef.current = setTimeout(() => {
        stopWatchingLocation();
        startWatchingLocation();
      }, durationMs);
    },
    [
      supported,
      hasPermission,
      requestPermission,
      stopWatchingLocation,
      startWatchingLocation,
    ],
  );

  const enterBaselinePrecision = useCallback(async () => {
    if (highPrecisionTimeoutRef.current) {
      clearTimeout(highPrecisionTimeoutRef.current);
      highPrecisionTimeoutRef.current = null;
    }
    stopWatchingLocation();
    await startWatchingLocation();
  }, [stopWatchingLocation, startWatchingLocation]);

  const ensurePreciseFix = useCallback(
    async (timeoutMs = 8_000) => {
      if (!supported) return;
      if (!hasPermission) {
        const granted = await requestPermission();
        if (!granted) return;
      }
      return new Promise<void>(resolve => {
        navigator.geolocation.getCurrentPosition(
          pos => {
            setLocation(coordsFrom(pos));
            setLastUpdatedAt(Date.now());
            resolve();
          },
          () => resolve(),
          { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 0 },
        );
      });
    },
    [supported, hasPermission, requestPermission],
  );

  const startWatchingHeading = useCallback(async () => {
    if (typeof window === 'undefined') return;
    const handler = (e: DeviceOrientationEvent) => {
      // alpha = compass heading (degrees)
      if (e.alpha !== null) setHeading(e.alpha);
      // No exact analogue to accuracy on web; webkit provides webkitCompassAccuracy
      const acc = (e as any).webkitCompassAccuracy;
      if (typeof acc === 'number') setHeadingAccuracy(acc);
    };
    headingHandlerRef.current = handler;
    window.addEventListener('deviceorientation', handler, true);
    setIsWatchingHeading(true);
  }, []);

  const stopWatchingHeading = useCallback(() => {
    if (headingHandlerRef.current && typeof window !== 'undefined') {
      window.removeEventListener(
        'deviceorientation',
        headingHandlerRef.current,
        true,
      );
      headingHandlerRef.current = null;
    }
    setIsWatchingHeading(false);
  }, []);

  const getLocation = useCallback(
    async (params: { search?: string; coords?: number[] }) => {
      const { data } = await httpClient.get(LOCATION_API, {
        params:
          params.search != null
            ? { search: params.search }
            : params.coords
              ? { coords: params.coords.join(',') }
              : {},
      });
      return data;
    },
    [],
  );

  // Probe permission on mount.
  useEffect(() => {
    checkPermission();
  }, [checkPermission]);

  // Cleanup watchers on unmount.
  useEffect(() => {
    return () => {
      stopWatchingLocation();
      stopWatchingHeading();
      if (highPrecisionTimeoutRef.current) {
        clearTimeout(highPrecisionTimeoutRef.current);
      }
    };
  }, [stopWatchingLocation, stopWatchingHeading]);

  const value = useMemo<LocationContextType>(
    () => ({
      location,
      lastUpdatedAt,
      isStale,
      loading,
      error,
      heading,
      headingAccuracy,
      hasPermission,
      permissionStatus,
      isRequesting,
      requiresSettingsAccess,
      checkPermission,
      requestPermission,
      refreshLocation,
      startWatchingLocation,
      stopWatchingLocation,
      startWatchingHeading,
      stopWatchingHeading,
      isWatchingHeading,
      ensureFreshLocation,
      enterHighPrecision,
      enterBaselinePrecision,
      ensurePreciseFix,
      getLocation,
    }),
    [
      location,
      lastUpdatedAt,
      isStale,
      loading,
      error,
      heading,
      headingAccuracy,
      hasPermission,
      permissionStatus,
      isRequesting,
      requiresSettingsAccess,
      isWatchingHeading,
      checkPermission,
      requestPermission,
      refreshLocation,
      startWatchingLocation,
      stopWatchingLocation,
      startWatchingHeading,
      stopWatchingHeading,
      ensureFreshLocation,
      enterHighPrecision,
      enterBaselinePrecision,
      ensurePreciseFix,
      getLocation,
    ],
  );

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};

export function useLocation(): LocationContextType {
  const ctx = useContext(LocationContext);
  if (!ctx) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return ctx;
}
